import { useState } from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverSpotlight } from "@/components/ui/hover-spotlight";
import CustomSolution from "@/components/CustomSolution";

interface PricingTierProps {
  name: string;
  description: string;
  price: number | string;
  currency: string;
  features: string[];
  whyChoose: string;
  callToAction: string;
  callToActionLink: string;
}

const pricingTiers: PricingTierProps[] = [
  {
    name: "Custom Development",
    description: "For businesses looking for a turnkey MVP solution",
    price: 3500,
    currency: "£",
    features: [
      "Completed MVP product/automation: We'll build and deliver a fully functional MVP tailored to your needs.",
      "Up to 3 core features: Pick up to three must-have features, expand later.",
      "Weekly strategic planning sessions: Steer the project with regular check-ins.",
      "AI integration setup: We'll integrate relevant AI tools or APIs to enhance your product's capabilities.",
      "Stripe & email integrations: Seamless payment processing and communication.",
      "Full ownership: You own 100% of everything we build."
    ],
    whyChoose: "Perfect for businesses looking for a turnkey MVP solution with professional guidance and guaranteed deliverables.",
    callToAction: "Book a Call",
    callToActionLink: "https://cal.com/niumora-ai/project-consultation"
  },
  {
    name: "AI Bootcamp",
    description: "For those wanting hands-on AI experience",
    price: 249,
    currency: "£",
    features: [
      "Daily group sessions (max 10 people): Personal engaging online video sessions.",
      "Automation templates: Ready-to-use workflows that can be tailored to your operations.",
      "Prompting template: Learn best practices for AI prompting to maximise results.",
      "Immediate value: Rapid insights and quick wins for your business.",
      "Support: 1 week email & whatsapp support",
      "Free community access: 1 year community access on launch"
    ],
    whyChoose: "Ideal if you want hands-on AI experience and the camaraderie (and cost-sharing) of a group environment.",
    callToAction: "Learn More",
    callToActionLink: "/ai-bootcamp"
  },
  {
    name: "Expert Training",
    description: "1 hour focused, one-on-one training and problem solving",
    price: 62,
    currency: "£",
    features: [
      "One-on-one Expert Session: Personalised session focused on No-code, Automation, or GenAI.",
      "Selected domain overview: We'll drill down into your specific domain to identify the best approach.",
      "Setup and prompting: Hands-on guidance with tools, integrations, and AI prompts.",
      "Instant value: Apply what you learn in real time, leave with tangible skills.",
      "Resources list: Curated list to keep you moving forward.",
      "Full ownership: Anything built or created during the session is entirely yours."
    ],
    whyChoose: "Perfect if you want focused, one-on-one help solving a specific problem or upskilling quickly.",
    callToAction: "Book a Session",
    callToActionLink: "https://cal.com/niumora-ai/60min-expert-session"
  }
];

const Pricing = () => {
  const [hoveredTier, setHoveredTier] = useState<number | null>(null);

  return (
    <section id="pricing" className="py-16 md:py-24 bg-white">
      <div className="container max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">
            Our <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Choose the plan that's right for your business. All plans include access to our core AI features.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {pricingTiers.map((tier, index) => (
            <HoverSpotlight
              key={index}
              className={cn(
                "relative rounded-2xl transition-all duration-300 border-2 border-primary shadow-lg",
                hoveredTier === index ? "shadow-xl" : "bg-white"
              )}
              onMouseEnter={() => setHoveredTier(index)}
              onMouseLeave={() => setHoveredTier(null)}
            >
              <div className="p-6 md:p-8 flex flex-col h-full">
                <div>
                  <div className="text-xl font-bold mb-2">{tier.name}</div>
                  <p className="text-muted-foreground text-sm mb-6">{tier.description}</p>
                  
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-primary">{tier.currency}{tier.price}</span>
                      <span className="ml-2 text-lg line-through text-muted-foreground">
                        {tier.currency}{typeof tier.price === 'number' ? tier.price * 2 : tier.price}
                      </span>
                      <span className="ml-2 px-2 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                        50% OFF
                      </span>
                    </div>
                    <div className="text-sm text-primary font-semibold mt-1">Launch Special Offer</div>
                  </div>
                  
                  <div className="space-y-4 mb-8">
                    <div className="text-sm font-semibold">What You Get:</div>
                    {tier.features.map((feature, i) => {
                      const parts = feature.split(":");
                      const firstPart = parts[0] + ":";
                      const restPart = parts.slice(1).join(":");
                      
                      return (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <Check className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                          <span>
                            <strong>{firstPart}</strong>
                            {restPart}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="space-y-4 mb-8">
                    <div className="text-sm font-semibold">Why choose this:</div>
                    <p className="text-sm">{tier.whyChoose}</p>
                  </div>
                </div>
                
                <div className="mt-auto">
                  <Button 
                    className={cn(
                      "w-full rounded-full",
                      tier.callToAction === "Learn More" 
                        ? "bg-white text-foreground border border-input hover:bg-accent hover:text-accent-foreground"
                        : "gradient-button text-white hover:scale-105 transition-all duration-300"
                    )}
                    variant={tier.callToAction === "Learn More" ? "outline" : "default"}
                    onClick={() => window.open(tier.callToActionLink, "_blank")}
                  >
                    {tier.callToAction}
                  </Button>
                </div>
              </div>
            </HoverSpotlight>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="mt-8">
            <CustomSolution />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
